"use client";

import React, { useState } from "react";
import { Shield, Activity, Upload, FileText, Building2, Stethoscope, House, TriangleAlert, CircleCheck, Clock, Info } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  ...props
}, ref) => {
  return <div data-section-id="44589" className="relative w-full">
        <select className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none ${className}`} ref={ref} {...props} />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4"><path d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
        </div>
      </div>;
});
Select.displayName = "Select";
const RiskForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-primary/10 border border-primary/20 rounded-xl animate-in fade-in zoom-in duration-300">
        <div className="h-16 w-16 bg-primary/20 rounded-full flex items-center justify-center">
          <CircleCheck className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-xl font-default font-semibold text-foreground">Proyecto Registrado</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <div className="text-sm text-primary bg-primary/10 px-4 py-2 rounded-full font-mono">
          NRO. COTIZACIÓN: #MIV-{Math.floor(Math.random() * 10000)}
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold font-mono">Gerente / Responsable</FormLabel>
              <FormControl>
                <Input placeholder="Ing. Carlos Rodríguez" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El email es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Email inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold font-mono">Correo Corporativo</FormLabel>
              <FormControl>
                <Input type="email" placeholder="proyectos@empresa.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold font-mono">Teléfono de Contacto</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+58 241 000 0000" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold font-mono">Planta / Empresa</FormLabel>
              <FormControl>
                <Input placeholder="Complejo Industrial Carabobo" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="risk_type" rules={{
      required: "Seleccione un tipo de servicio"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold font-mono">Tipo de Servicio Industrial</FormLabel>
            <FormControl>
              <Select {...field} value={field.value || ""}>
                <option value="" disabled>Seleccione una especialidad...</option>
                <option value="Mecanizado de Precisión">Mecanizado de Precisión CNC</option>
                <option value="Fabricación Mecánica">Fabricación y Reparación Mecánica</option>
                <option value="Engranajes y Cajas Reductoras">Engranajes y Cajas Reductoras</option>
                <option value="Intercambiadores de Calor">Intercambiadores de Calor</option>
                <option value="Rebabitado de Cojinetes">Rebabitado de Cojinetes</option>
                <option value="Mecanizado en Sitio">Mecanizado en Sitio (On-site)</option>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "El detalle es obligatorio"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold font-mono">Especificaciones Técnicas / Alcance</FormLabel>
            <FormControl>
              <Textarea placeholder="Describa el tipo de pieza, material, dimensiones, tolerancias requeridas y urgencia del proyecto..." className="min-h-[120px] font-mono text-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="space-y-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold font-mono">Planos CAD / Especificaciones (PDF/DWG)</span>
        <div className="border-2 border-dashed border-border/40 rounded-lg p-6 flex flex-col items-center justify-center text-center hover:bg-accent/5 transition-colors cursor-pointer group">
          <div className="h-10 w-10 bg-accent/10 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <Upload className="h-5 w-5 text-primary" />
          </div>
          <p className="text-sm font-medium text-foreground">Arrastre archivos aquí o haga clic para examinar</p>
          <p className="text-xs text-muted-foreground mt-1">PDF, DWG, STEP, JPG (Máx. 50MB)</p>
        </div>
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2">
          <TriangleAlert className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <Button type="submit" className="w-full h-12 text-base font-semibold shadow-lg shadow-primary/20 bg-primary text-primary-foreground hover:opacity-90 transition-opacity rounded-sm" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Activity className="mr-2 h-4 w-4 animate-spin" />
            Procesando Solicitud...
          </> : <>
            <Shield className="mr-2 h-4 w-4" />
            Obtener Cotización Inmediata
          </>}
      </Button>
      
      <p className="text-xs text-center text-muted-foreground font-sans">
        Al enviar este formulario, usted autoriza el tratamiento de sus datos técnicos para fines de cotización y consultoría de ingeniería.
      </p>
    </Form>;
};
export default function RiskManagementCenter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [selectedInfra, setSelectedInfra] = useState<string | null>(null);
  const infrastructureTypes = [{
    id: "cnc",
    label: "Mecanizado CNC",
    icon: Building2,
    desc: "Torneado, fresado y mecanizado CNC de alta precisión para componentes de petróleo, gas y petroquímica."
  }, {
    id: "heavy",
    label: "Fabricación y Reparación Pesada",
    icon: Stethoscope,
    desc: "Fabricación estructural a gran escala y reparación de ejes para la industria pesada e instalaciones de cemento."
  }, {
    id: "onsite",
    label: "Servicios Industriales en Sitio",
    icon: House,
    desc: "Mantenimiento de planta de emergencia y mecanizado en sitio para instalaciones petroquímicas e industria pesada."
  }, {
    id: "rebabitado",
    label: "Rebabitado de Cojinetes",
    icon: Building2,
    desc: "Recuperación técnica de cojinetes, chumaceras y superficies antifricción mediante rebabitado, mecanizado de precisión y control dimensional."
  }];
  return <section id="contacto" className="relative w-full py-12 lg:py-20 bg-background text-foreground overflow-hidden dark px-5 lg:px-0" ref={ref}>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      
      <div className="container mx-auto relative z-10">
        <div className={`max-w-4xl mx-auto mb-12 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-wider mb-6 backdrop-blur-sm font-mono">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            Planta MIV: Operativa
          </div>
          
          <h2 className="font-default tracking-tight text-foreground mb-4 uppercase !font-medium text-xl lg:text-2xl leading-tight lg:leading-normal">MECANIZADO, FABRICACIÓN, REPARACIÓN Y MANTENIMIENTO INDUSTRIAL PARA PETRÓLEO Y GAS, PETROQUÍMICA E INDUSTRIA PESADA</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-sans text-sm lg:text-base">Nuestra unidad de ingeniería entrega componentes mecánicos de alta precisión y reparaciones de respuesta rápida diseñadas para las exigentes demandas de los sectores de petróleo y gas y petroquímico. Garantizamos la continuidad operativa mediante una fabricación confiable y mantenimiento experto, minimizando el tiempo de inactividad en entornos industriales críticos.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-6xl mx-auto">
          <div className={`lg:col-span-5 space-y-6 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <Card className="bg-card/30 backdrop-blur-sm border-border/40 shadow-sm rounded-sm w-full">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase font-mono">Factibilidad de ANÁLISIS</p>
                    <p className="text-xl font-bold text-foreground font-default">&lt; 24 Hours</p>
                  </div>
                </div>
                <Badge variant="outline" className="bg-background/50 border-primary/30 text-primary hidden sm:inline-flex">Prioridad Industrial</Badge>
              </CardContent>
            </Card>

            <Card className="bg-card/30 backdrop-blur-sm border-border/40 shadow-sm rounded-sm w-full">
              
            </Card>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2 font-mono">
                <Info className="h-4 w-4 text-primary" />
                Seleccione su Categoría de Servicio
              </h3>
              <div className="grid gap-3">
                {infrastructureTypes.map((type, i) => <div key={type.id} data-index={i} onClick={() => setSelectedInfra(type.id)} className={`
                      relative group cursor-pointer rounded-sm border p-4 transition-all duration-300 w-full
                      ${selectedInfra === type.id ? 'bg-primary/5 border-primary shadow-md ring-1 ring-primary/20' : 'bg-card/40 border-border/40 hover:border-primary/50 hover:shadow-sm'}
                    `}>
                    <div className="flex items-center gap-4">
                      <div className={`
                        p-3 rounded-sm transition-colors shrink-0
                        ${selectedInfra === type.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:text-foreground'}
                      `}>
                        <type.icon className="h-6 w-6" />
                      </div>
                      <div className="pr-6">
                        <h4 className="font-default font-semibold text-foreground uppercase text-sm tracking-wide">{type.label}</h4>
                        <p className="text-xs text-muted-foreground font-sans">{type.desc}</p>
                      </div>
                      {selectedInfra === type.id && <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <CircleCheck className="h-5 w-5 text-primary" />
                        </div>}
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="bg-muted/10 rounded-sm p-4 border border-border/20">
              <div className="flex gap-3">
                <FileText className="h-5 w-5 text-primary shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed font-sans">
                  <strong className="text-foreground font-mono">Nota Técnica:</strong> Para piezas que requieran tolerancias inferiores a 0,01 mm, los modelos 3D (STEP/IGES) son obligatorios. Nuestro equipo de metrología verifica todas las especificaciones para garantizar la precisión antes de la cotización final.
                </p>
              </div>
            </div>
          </div>

          <div className={`lg:col-span-7 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <Card className="text-card-foreground flex flex-col gap-6 py-6 border-t-4 border-t-primary shadow-2xl shadow-black/40 bg-card/90 backdrop-blur-md rounded-sm w-full max-w-4xl mx-auto">
              <CardHeader className="border-b border-border/20 pb-6 px-4 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle id="formulario-cotizacion-industrial" className="text-lg lg:text-xl font-default font-bold uppercase tracking-tight scroll-mt-24">SOLICITE UNA COTIZACIÓN INDUSTRIAL INMEDIATA</CardTitle>
                    <CardDescription className="font-sans mt-2">Cotización rápida y precisa para proyectos industriales complejos.<br />MIV atención telefónica:<br />Cel 0414-6840865</CardDescription>
                  </div>
                  <div className="text-left sm:text-right">
                    
                    
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 px-4 sm:px-6">
                <FormProvider formKey="industrial_project_form_home" formId="industrial_project_form_home" sectionName="risk-management-section" defaultValues={{
                name: "",
                email: "",
                phone: "",
                organization: "",
                risk_type: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                risk_type: {
                  required: true
                },
                message: {
                  required: true
                }
              }} submitText="Enviar Solicitud" successMessage="Su solicitud ha sido registrada exitosamente. Un ingeniero de nuestro departamento de producción evaluará las especificaciones y se pondrá en contacto para la fase de cotización.">
                  <RiskForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}